// CFO-Controlling-Export — pure, read-only builders.
//
// Erzeugt mehrere CSV-Dateien für die Effizienz-/Margen-/Auslastungsanalyse.
// Bewusst eigene Format-Konventionen (NICHT die der Monats-Auswertung), damit
// sich die Dateien sauber per IDs joinen und mit externem Umsatz (sevDesk)
// verrechnen lassen:
//   - Trennzeichen: Semikolon (;)
//   - Kodierung:    UTF-8 mit BOM (beim Download/ZIP vorangestellt)
//   - Datum:        ISO YYYY-MM-DD
//   - Stunden:      Dezimalzahl mit PUNKT (z. B. 1.50)
//   - Beträge:      Dezimalzahl mit PUNKT, ohne Währungssymbol (EUR im Header)
//   - Booleans:     true / false
//   - Fehlende "falls vorhanden"-Felder: Spalte bleibt leer, Header bleibt stehen.
//
// Keinerlei Schreibzugriff, keine Mutationen — alles aus bereits geladenen Daten.

import { durationMin, todayIso, pad } from './format.js';

// --- Zell-/Wert-Formatierung ------------------------------------------------

// CSV-Escaping nach RFC 4180: bei ; " oder Zeilenumbruch in Anführungszeichen
// setzen und enthaltene " verdoppeln.
function cell(v) {
  const s = v == null ? '' : String(v);
  return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function row(cells) {
  return cells.map(cell).join(';');
}

// Stunden aus Minuten, 2 Nachkommastellen, Dezimalpunkt.
function hoursFromMin(min) {
  return (Math.round((min / 60) * 100) / 100).toFixed(2);
}

// Beliebige Zahl, 2 Nachkommastellen, Dezimalpunkt. Leer bei null/undefined.
function num2(n) {
  if (n == null || n === '' || Number.isNaN(Number(n))) return '';
  return (Math.round(Number(n) * 100) / 100).toFixed(2);
}

function bool(v) {
  if (v == null) return '';
  return v ? 'true' : 'false';
}

// Timestamptz / Date → ISO YYYY-MM-DD (lokale Kalenderdaten; entry_date ist
// bereits ein reines Datum, created_at/completed_at sind Zeitstempel).
function isoDate(v) {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// --- Datei-Builder ----------------------------------------------------------
//
// Jeder Builder bekommt das geladene Datenmodell + einen optionalen Zeitraum
// { from, to } (ISO-Strings, inklusiv) und gibt den fertigen CSV-Text zurück.

// Datei 1 — time_entries.csv (höchste Priorität)
export function buildTimeEntriesCsv(data, range) {
  const { timeEntries, profiles, projects, customers } = data;
  const profileById = idMap(profiles);
  const projectById = idMap(projects);
  const customerById = idMap(customers);

  const header = [
    'entry_id', 'date', 'employee_id', 'employee_name',
    'project_id', 'project_name', 'customer_id', 'customer_name',
    'task_id', 'task_or_activity', 'hours', 'billable', 'billed',
    'hourly_rate', 'description',
  ];
  const lines = [row(header)];

  const entries = inRange(timeEntries, 'entry_date', range)
    .slice()
    .sort((a, b) => (a.entry_date || '').localeCompare(b.entry_date || '')
      || (a.from_time || '').localeCompare(b.from_time || ''));

  for (const te of entries) {
    const emp = profileById[te.user_id];
    const proj = te.project_id ? projectById[te.project_id] : null;
    // Kunde: bei Projekt-Einträgen über das Projekt, bei Meetings direkt am Eintrag.
    const customerId = te.customer_id || proj?.customer_id || '';
    const cust = customerId ? customerById[customerId] : null;
    const min = durationMin(te.from_time, te.to_time);

    lines.push(row([
      te.id,
      isoDate(te.entry_date),
      te.user_id || '',
      emp?.full_name || '',
      te.project_id || '',
      proj?.title || '',
      customerId,
      cust?.name || '',
      '', // task_id: Zeiteinträge sind im System NICHT mit einer Aufgabe verknüpft
      te.kind || (te.project_id ? 'project' : 'support'), // task_or_activity = Tätigkeitsart
      hoursFromMin(min),
      bool(te.billable),
      // "billed" gibt es nicht pro Eintrag; Projekt-Flag 'invoiced' ist das Nächste.
      proj ? bool(proj.invoiced) : '',
      // Pro-Eintrag-Satz gibt es nicht; hinterlegt ist der Satz des Mitarbeiters.
      num2(emp?.hourly_rate),
      te.description || '',
    ]));
  }
  return lines.join('\r\n');
}

// Datei 2 — projects.csv (alle Projekte, inkl. archiviert)
export function buildProjectsCsv(data) {
  const { projects, customers } = data;
  const customerById = idMap(customers);

  const header = [
    'project_id', 'project_name', 'customer_id', 'customer_name',
    'type', 'status', 'start_date', 'end_date',
    'budget_hours', 'budget_amount_eur', 'agreed_hourly_rate',
    'responsible_employee_id',
    // Zusätzliche, für Margenrechnung hochrelevante Felder aus dem System:
    'budget_is_estimate', 'billable', 'invoiced', 'invoiced_at', 'deadline',
  ];
  const lines = [row(header)];

  for (const p of projects) {
    const cust = p.customer_id ? customerById[p.customer_id] : null;
    lines.push(row([
      p.id,
      p.title || '',
      p.customer_id || '',
      cust?.name || '',
      '', // type: keine Projektart im System (Retainer/Festpreis/T&M nicht erfasst)
      p.status || '',
      isoDate(p.created_at),  // start_date-Proxy: echtes Startdatum fehlt → Anlagedatum
      isoDate(p.completed_at), // end_date: Abschlussdatum, falls geschlossen
      p.budget_minutes != null ? num2(p.budget_minutes / 60) : '',
      num2(p.budget_euro),
      '', // agreed_hourly_rate: kein projektbezogener Satz (Satz liegt am Mitarbeiter)
      p.created_by || '', // responsible-Proxy: Ersteller/Owner des Projekts
      bool(p.budget_is_estimate),
      bool(p.billable),
      bool(p.invoiced),
      isoDate(p.invoiced_at),
      isoDate(p.deadline),
    ]));
  }
  return lines.join('\r\n');
}

// Datei 3 — employees.csv
export function buildEmployeesCsv(data) {
  const { profiles } = data;
  const header = [
    'employee_id', 'employee_name', 'role', 'active',
    'weekly_target_hours', 'start_date', 'end_date',
    'hourly_rate_eur', 'email',
  ];
  const lines = [row(header)];

  for (const p of profiles) {
    lines.push(row([
      p.id,
      p.full_name || '',
      p.role || '',
      // Kein Inaktiv-/Austritts-Flag im System → alle als aktiv ausgewiesen.
      'true',
      num2(p.weekly_hours),
      isoDate(p.created_at), // start_date-Proxy: kein Eintrittsdatum → Anlagedatum
      '', // end_date: kein Austrittsdatum im System
      num2(p.hourly_rate),
      p.email || '',
    ]));
  }
  return lines.join('\r\n');
}

// Datei 4 — tasks.csv (optional, Kapazitätsplanung)
export function buildTasksCsv(data) {
  const { tasks } = data;
  const header = [
    'task_id', 'project_id', 'title', 'assignee_id',
    'estimate_hours', 'status', 'due_date',
    // Zusatz: wer/wann erledigt (im System statt assignee vorhanden).
    'done_by_id', 'done_at',
  ];
  const lines = [row(header)];

  for (const t of tasks) {
    lines.push(row([
      t.id,
      t.project_id || '',
      t.title || '',
      '', // assignee_id: keine Zuweisung im System (nur "erledigt von")
      '', // estimate_hours: kein Schätzfeld im System
      t.done ? 'done' : 'open',
      '', // due_date: kein Fälligkeitsdatum auf Aufgaben-Ebene
      t.done_by || '',
      isoDate(t.done_at),
    ]));
  }
  return lines.join('\r\n');
}

// Nice-to-have — utilization_monthly.csv: pro Mitarbeiter & Monat
// gebuchte Stunden, davon billable, Soll-Stunden (aus Wochen-Soll minus
// Abwesenheiten geschätzt). Spart die Vorverdichtung.
export function buildUtilizationMonthlyCsv(data, range) {
  const { timeEntries, profiles, absences } = data;
  const header = [
    'employee_id', 'employee_name', 'month',
    'hours_total', 'hours_billable', 'target_hours',
  ];
  const lines = [row(header)];

  // 1) Gebuchte Stunden je (user, Monat) aufsummieren.
  const agg = {}; // key `${uid}|${YYYY-MM}` → { min, billableMin }
  for (const te of inRange(timeEntries, 'entry_date', range)) {
    const month = (te.entry_date || '').slice(0, 7);
    if (!month) continue;
    const key = `${te.user_id}|${month}`;
    const slot = (agg[key] ||= { min: 0, billableMin: 0 });
    const min = durationMin(te.from_time, te.to_time);
    slot.min += min;
    if (te.billable) slot.billableMin += min;
  }

  // 2) Monatsspanne bestimmen (aus Range oder aus den Daten).
  const months = monthSpan(timeEntries, range);

  // 3) Eine Zeile je Mitarbeiter × Monat (auch ohne Buchung → 0 Stunden,
  //    damit Auslastung = Ist/Soll lückenlos rechenbar ist).
  for (const p of profiles) {
    const weekly = Number(p.weekly_hours) || 0;
    for (const month of months) {
      const key = `${p.id}|${month}`;
      const slot = agg[key] || { min: 0, billableMin: 0 };
      const target = targetHoursForMonth(month, weekly, p.id, absences);
      lines.push(row([
        p.id,
        p.full_name || '',
        month,
        hoursFromMin(slot.min),
        hoursFromMin(slot.billableMin),
        num2(target),
      ]));
    }
  }
  return lines.join('\r\n');
}

// Datei 5 — README_export.txt
export function buildReadme(data, range, generatedAtIso) {
  const { timeEntries } = data;
  const inRangeEntries = inRange(timeEntries, 'entry_date', range);
  const totalMin = inRangeEntries.reduce((s, te) => s + durationMin(te.from_time, te.to_time), 0);
  const rangeLabel = range && (range.from || range.to)
    ? `${range.from || 'Anfang'} bis ${range.to || 'heute'}`
    : 'Gesamter Zeitraum (alle Daten)';

  return [
    'CFO-Controlling-Export — pm.markenegger.com',
    '===========================================',
    '',
    `Exportzeitpunkt:        ${generatedAtIso}`,
    `Gewählter Zeitraum:     ${rangeLabel}`,
    `Zeiteinträge im Export: ${inRangeEntries.length}`,
    `Summe Stunden:          ${hoursFromMin(totalMin)} h`,
    '',
    'FORMAT',
    '------',
    '- Trennzeichen: Semikolon (;)',
    '- Kodierung: UTF-8 mit BOM (öffnet sauber in Excel, inkl. Umlaute)',
    '- Datum: ISO YYYY-MM-DD',
    '- Stunden: Dezimal mit Punkt (z. B. 1.50), NICHT hh:mm',
    '- Beträge: Dezimal mit Punkt, ohne Währungssymbol; Währung = EUR',
    '- Booleans: true / false',
    '- Zeitzone: Datumsfelder sind reine Kalenderdaten (keine Uhrzeit/TZ-Umrechnung).',
    '',
    'JOIN-SCHLÜSSEL',
    '--------------',
    '- time_entries.employee_id  -> employees.employee_id',
    '- time_entries.project_id   -> projects.project_id   (leer bei Meeting/Begleitaufgabe)',
    '- time_entries.customer_id  -> (Kunde; aus Projekt oder direkt am Eintrag)',
    '- projects.customer_id      -> (Kunde)',
    '- tasks.project_id          -> projects.project_id',
    '',
    'WICHTIGE HINWEISE ZU "FALLS VORHANDEN"-FELDERN',
    '----------------------------------------------',
    'Folgende Felder existieren im System NICHT und bleiben daher leer',
    '(Spalte/Headerstruktur bleibt für ein stabiles Format erhalten):',
    '',
    'time_entries.csv:',
    '  - task_id: Zeiteinträge sind NICHT mit einer Aufgabe verknüpft. Stattdessen',
    '    steht in "task_or_activity" die Tätigkeitsart (kind): project | meeting | support.',
    '  - billed: Es gibt KEIN Abgerechnet-Flag pro Eintrag. Ersatzweise wird das',
    '    projektweite Flag "invoiced" ausgegeben (bei projektlosen Einträgen leer).',
    '  - hourly_rate: Es gibt KEINEN Satz pro Eintrag. Ausgegeben wird der am',
    '    Mitarbeiter hinterlegte Verrechnungssatz (profiles.hourly_rate). Der gleiche',
    '    Satz steht auch in employees.hourly_rate_eur.',
    '  - billable: existiert pro Eintrag (true/false) und ist gefüllt.',
    '',
    'projects.csv:',
    '  - type: Keine Projektart erfasst (kein Retainer/Festpreis/T&M-Feld). Leer.',
    '  - start_date: Kein echtes Startdatum; ausgegeben wird das Anlagedatum (created_at).',
    '  - end_date: Abschlussdatum (completed_at), nur bei geschlossenen Projekten gefüllt.',
    '  - agreed_hourly_rate: Kein projektbezogener Satz; Satz liegt am Mitarbeiter. Leer.',
    '  - responsible_employee_id: Kein dediziertes Feld; ausgegeben wird created_by',
    '    (Ersteller/Owner des Projekts).',
    '  - Zusatzspalten: budget_is_estimate (Budget nur geschätzt?), billable, invoiced,',
    '    invoiced_at, deadline — für die Margen-/Abrechnungsanalyse.',
    '  - budget_hours = budget_minutes / 60. Alle Projekte enthalten (auch archiviert),',
    '    Status in der Spalte "status" (active|closing|done|on_hold|cancelled|archived).',
    '',
    'employees.csv:',
    '  - active: Kein Inaktiv-/Austritts-Flag im System -> alle als "true" ausgewiesen.',
    '  - start_date: Kein Eintrittsdatum; ausgegeben wird das Anlagedatum (created_at).',
    '  - end_date: Kein Austrittsdatum im System. Leer.',
    '  - weekly_target_hours = profiles.weekly_hours (Soll-Wochenstunden / Pensum).',
    '',
    'tasks.csv:',
    '  - assignee_id: Keine Zuweisung im System. Stattdessen done_by_id / done_at',
    '    (wer hat die Aufgabe erledigt, wann).',
    '  - estimate_hours: Kein Schätzfeld. Leer.',
    '  - due_date: Kein Fälligkeitsdatum auf Aufgaben-Ebene. Leer.',
    '  - status: aus dem done-Flag abgeleitet (open | done).',
    '',
    'utilization_monthly.csv (Verdichtung):',
    '  - Eine Zeile je Mitarbeiter × Monat (auch Monate ohne Buchung = 0 h).',
    '  - hours_total / hours_billable: Summe der gebuchten bzw. abrechenbaren Stunden.',
    '  - target_hours: geschätztes Monats-Soll = (weekly_hours / 5) × Werktage(Mo–Fr)',
    '    des Monats, abzüglich der als Urlaub/Krank erfassten Werktage. Näherung,',
    '    keine Feiertagslogik.',
    '',
    'STUNDENBERECHNUNG',
    '-----------------',
    'Stunden = (to_time - from_time) je Eintrag. Es werden alle Tätigkeitsarten',
    'gezählt (project, meeting, support). Für reine Projektstunden in',
    'time_entries.csv nach project_id <> leer filtern.',
    '',
  ].join('\r\n');
}

// --- Hilfsfunktionen --------------------------------------------------------

function idMap(rows) {
  const m = {};
  for (const r of rows) m[r.id] = r;
  return m;
}

// Filtert Zeilen auf einen inklusiven Datumsbereich (ISO-Strings).
function inRange(rows, dateField, range) {
  if (!range || (!range.from && !range.to)) return rows;
  return rows.filter(r => {
    const d = r[dateField];
    if (!d) return false;
    if (range.from && d < range.from) return false;
    if (range.to && d > range.to) return false;
    return true;
  });
}

// Liste aller Monate (YYYY-MM) im Bereich; ohne Bereich aus dem Datenbestand.
function monthSpan(timeEntries, range) {
  let start, end;
  if (range && range.from) start = range.from.slice(0, 7);
  if (range && range.to) end = range.to.slice(0, 7);
  if (!start || !end) {
    let min = null, max = null;
    for (const te of timeEntries) {
      const m = (te.entry_date || '').slice(0, 7);
      if (!m) continue;
      if (min == null || m < min) min = m;
      if (max == null || m > max) max = m;
    }
    start = start || min;
    end = end || max;
  }
  if (!start || !end) return [];
  const months = [];
  let [y, m] = start.split('-').map(Number);
  const [ey, em] = end.split('-').map(Number);
  while (y < ey || (y === ey && m <= em)) {
    months.push(`${y}-${pad(m)}`);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return months;
}

// Geschätztes Monats-Soll: (Wochen-Soll / 5) × Werktage(Mo–Fr) im Monat,
// minus die als Urlaub/Krank erfassten Werktage dieses Mitarbeiters.
function targetHoursForMonth(month, weeklyHours, userId, absences) {
  if (!weeklyHours) return 0;
  const [y, m] = month.split('-').map(Number);
  const daily = weeklyHours / 5;
  const daysInMonth = new Date(y, m, 0).getDate();
  let workdays = 0;
  let absentWorkdays = 0;
  for (let day = 1; day <= daysInMonth; day++) {
    const dt = new Date(y, m - 1, day);
    const dow = dt.getDay(); // 0=So .. 6=Sa
    if (dow === 0 || dow === 6) continue;
    workdays += 1;
    const iso = `${y}-${pad(m)}-${pad(day)}`;
    if (isAbsentOn(userId, iso, absences)) absentWorkdays += 1;
  }
  return Math.max(0, (workdays - absentWorkdays) * daily);
}

function isAbsentOn(userId, iso, absences) {
  for (const a of absences) {
    if (a.user_id !== userId) continue;
    if (iso >= a.start_date && iso <= a.end_date) return true;
  }
  return false;
}

// --- Bündelung + Download ---------------------------------------------------

// Baut alle Export-Dateien als { name, text }-Liste.
export function buildAllFiles(data, range) {
  const generatedAt = new Date().toISOString();
  return [
    { name: 'time_entries.csv', text: buildTimeEntriesCsv(data, range) },
    { name: 'projects.csv', text: buildProjectsCsv(data) },
    { name: 'employees.csv', text: buildEmployeesCsv(data) },
    { name: 'tasks.csv', text: buildTasksCsv(data) },
    { name: 'utilization_monthly.csv', text: buildUtilizationMonthlyCsv(data, range) },
    { name: 'README_export.txt', text: buildReadme(data, range, generatedAt) },
  ];
}

// Einzeldownload einer CSV/TXT-Datei mit UTF-8-BOM.
export function downloadFile(filename, text) {
  if (typeof document === 'undefined') return;
  const isText = filename.endsWith('.txt');
  const blob = new Blob(['﻿' + text], {
    type: isText ? 'text/plain;charset=utf-8;' : 'text/csv;charset=utf-8;',
  });
  triggerDownload(filename, blob);
}

// Alle Dateien als ein ZIP (Store-Methode, keine Kompression — keine externe
// Library nötig). Jede Datei erhält ein vorangestelltes UTF-8-BOM.
export function downloadZip(filename, files) {
  if (typeof document === 'undefined') return;
  const blob = makeZipBlob(files);
  triggerDownload(filename, blob);
}

function triggerDownload(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Vorschlag für den Dateinamen mit heutigem Datum.
export function exportStamp() {
  return todayIso();
}

// --- Minimaler ZIP-Writer (Store / Methode 0) -------------------------------
//
// Schreibt ein gültiges ZIP ohne Kompression. Reicht für CSVs und vermeidet
// jede zusätzliche Abhängigkeit. CRC32 + lokale Header + Central Directory.

function crc32(bytes) {
  let crc = ~0;
  for (let i = 0; i < bytes.length; i++) {
    crc ^= bytes[i];
    for (let j = 0; j < 8; j++) {
      crc = (crc >>> 1) ^ (0xEDB88320 & -(crc & 1));
    }
  }
  return (~crc) >>> 0;
}

function makeZipBlob(files) {
  const enc = new TextEncoder();
  const parts = [];           // Blob-Teile in Reihenfolge
  const central = [];         // Central-Directory-Records
  let offset = 0;

  for (const f of files) {
    const nameBytes = enc.encode(f.name);
    const dataBytes = enc.encode('﻿' + f.text); // BOM pro Datei
    const crc = crc32(dataBytes);
    const size = dataBytes.length;

    const local = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true); // local file header signature
    lv.setUint16(4, 20, true);          // version needed
    lv.setUint16(6, 0x0800, true);      // flags: bit 11 = UTF-8 Dateinamen
    lv.setUint16(8, 0, true);           // method 0 = store
    lv.setUint16(10, 0, true);          // mod time
    lv.setUint16(12, 0, true);          // mod date
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true);       // compressed size
    lv.setUint32(22, size, true);       // uncompressed size
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true);          // extra length
    local.set(nameBytes, 30);

    parts.push(local, dataBytes);

    const cd = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(cd.buffer);
    cv.setUint32(0, 0x02014b50, true);  // central dir signature
    cv.setUint16(4, 20, true);          // version made by
    cv.setUint16(6, 20, true);          // version needed
    cv.setUint16(8, 0x0800, true);      // flags
    cv.setUint16(10, 0, true);          // method
    cv.setUint16(12, 0, true);          // mod time
    cv.setUint16(14, 0, true);          // mod date
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, 0, true);          // extra length
    cv.setUint16(32, 0, true);          // comment length
    cv.setUint16(34, 0, true);          // disk number start
    cv.setUint16(36, 0, true);          // internal attrs
    cv.setUint32(38, 0, true);          // external attrs
    cv.setUint32(42, offset, true);     // offset of local header
    cd.set(nameBytes, 46);
    central.push(cd);

    offset += local.length + dataBytes.length;
  }

  const centralSize = central.reduce((s, c) => s + c.length, 0);
  const centralOffset = offset;

  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);    // end of central dir signature
  ev.setUint16(4, 0, true);             // disk number
  ev.setUint16(6, 0, true);             // central dir disk
  ev.setUint16(8, files.length, true);  // entries on this disk
  ev.setUint16(10, files.length, true); // total entries
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, centralOffset, true);
  ev.setUint16(20, 0, true);            // comment length

  return new Blob([...parts, ...central, end], { type: 'application/zip' });
}
